/**
 * Sign-in without a Microsoft-hosted page.
 *
 * Entra native authentication lets us own the screens: the associate types their
 * Lowe's email, gets a code, types the code, and is in. They never see a sign-in
 * page that belongs to someone else, and never an "account creation" step - even
 * though a directory record is created for them the first time.
 *
 * That last part is the reason `beginEmailAuth` exists. Entra models first-time
 * and returning users as two different flows (sign-up vs sign-in) and there is no
 * combined entry point, so we try sign-in and quietly fall back to sign-up. From
 * the associate's side both look identical: enter email, enter code.
 *
 * SKU-1566.
 */
import type { AccountInfo } from '@azure/msal-browser';
import {
    CustomAuthPublicClientApplication,
    type ICustomAuthPublicClientApplication,
    type SignInCodeRequiredState,
    type SignUpCodeRequiredState,
} from '@azure/msal-browser/custom-auth';

import { loginRequest, type AppConfig } from './identity';

/** A state that is waiting for the emailed code, from either underlying flow. */
export type CodeState = SignInCodeRequiredState | SignUpCodeRequiredState;

export interface CodeRequired {
    kind: 'code';
    state: CodeState;
    /** How many digits Entra sent, so the input can size itself honestly. */
    codeLength: number;
}

export interface AuthFailed {
    kind: 'error';
    message: string;
}

export type BeginResult = CodeRequired | AuthFailed;

export async function createAuthClient(
    cfg: AppConfig,
): Promise<ICustomAuthPublicClientApplication> {
    const tenant = cfg.tenantId || cfg.tenantSubdomain;
    return CustomAuthPublicClientApplication.create({
        auth: {
            clientId: cfg.clientId,
            authority: `https://${tenant}.ciamlogin.com/${tenant}`,
            knownAuthorities: [`${tenant}.ciamlogin.com`],
        },
        cache: {
            // Sign in once per device, not once per shift.
            cacheLocation: 'localStorage',
        },
        customAuth: {
            // Same-origin proxy - the native auth endpoints send no CORS headers,
            // so the browser cannot call ciamlogin.com directly. See server.js.
            authApiProxyUrl: `${window.location.origin}/ciam`,
            // 'oob' is the emailed one-time passcode. 'redirect' is the mandatory
            // bail-out Entra uses to say "this one needs the hosted page after all".
            challengeTypes: ['oob', 'redirect'],
        },
    });
}

/**
 * Turn an Entra failure into something a person standing in a garden centre can
 * act on.
 *
 * Raw AADSTS strings carry trace IDs and internal wording and must never reach
 * an associate, so the original is logged for support and a plain sentence is
 * returned. Anything unrecognised falls back to the caller's wording rather than
 * leaking the underlying text.
 */
function describe(error: unknown, fallback: string): string {
    if (!error || typeof error !== 'object') return fallback;

    const e = error as {
        errorDescription?: string;
        errorData?: { errorDescription?: string };
        message?: string;
    };
    const raw = e.errorDescription ?? e.errorData?.errorDescription ?? e.message ?? '';
    if (raw) console.warn('auth failure:', raw);

    // 501811: wrong code, or the flow it belonged to has already expired.
    if (raw.includes('AADSTS501811')) {
        return 'That code did not work. It may have expired - send a new one and enter it straight away.';
    }
    // 50034: no such account, which our sign-up fallback should already handle.
    if (raw.includes('AADSTS50034')) {
        return 'We could not find or create an account for that address.';
    }
    /*
     * A leftover account in this browser's MSAL cache blocks both flows before
     * they reach the network. `beginEmailAuth` clears it first, so reaching here
     * means the clear itself failed. Whatever we say must not send the associate
     * off checking an address that was never the problem.
     */
    if (/already signed in/i.test(raw)) {
        return 'This browser is still holding an old sign-in. Close and reopen the page, or clear the site data, then try again.';
    }
    return fallback;
}

/**
 * Ask Entra to email a code to `email`, creating the account if this is their
 * first time. Returns the state that accepts the code, or a message to show.
 */
export async function beginEmailAuth(
    client: ICustomAuthPublicClientApplication,
    email: string,
): Promise<BeginResult> {
    /*
     * Clear any account left in this browser's MSAL cache before starting.
     *
     * The session cookie is the app's source of truth (see App.tsx) and MSAL's
     * cache is never consulted - but it also was never cleaned up, so the two
     * could diverge: cookie gone, account still in localStorage. Both signIn()
     * and signUp() call ensureUserNotSignedIn() as their first statement and
     * fail before any network call when that account exists, which made
     * sign-in permanently impossible on that device and reported it as a bad
     * email address. Reaching SignInPanel at all means we are not signed in as
     * far as this app is concerned, so anything MSAL still holds is stale.
     */
    await signOut(client);

    // openid is required or CustomAuthAccountData.getIdToken() returns
    // undefined later - the whole flow completes but leaves us with no way to
    // start a session. See SignInPanel.verify().
    const signIn = await client.signIn({ username: email, scopes: loginRequest.scopes });

    if (signIn.isCodeRequired()) {
        return {
            kind: 'code',
            state: signIn.state,
            codeLength: signIn.state.getCodeLength(),
        };
    }

    // Not a known account - or something else went wrong. Try creating one; if
    // that also fails, the sign-in error is the more meaningful of the two.
    // SignUpInputs has no scopes field - sign-up's tokens come later, from the
    // continuation.signIn() call in finish(), which is where scopes apply.
    const signUp = await client.signUp({ username: email });

    if (signUp.isCodeRequired()) {
        return {
            kind: 'code',
            state: signUp.state,
            codeLength: signUp.state.getCodeLength(),
        };
    }

    return {
        kind: 'error',
        message: describe(
            signIn.error ?? signUp.error,
            'We could not send a code to that address. Check it and try again.',
        ),
    };
}

export type SubmitOutcome =
    | { ok: true; account: AccountInfo | null; idToken?: string }
    | { ok: false; message: string; detail?: string };

/**
 * The untranslated failure text, for the diagnostic disclosure in the POC.
 *
 * Kept separate from `describe` so the friendly sentence stays the thing people
 * read, while the raw AADSTS string is one tap away instead of requiring
 * devtools. Remove the disclosure before this is in front of real associates.
 */
export function rawDetail(error: unknown): string | undefined {
    if (!error || typeof error !== 'object') return undefined;
    const e = error as {
        errorDescription?: string;
        errorData?: { errorDescription?: string };
        message?: string;
        errorCodes?: number[];
    };
    return e.errorDescription ?? e.errorData?.errorDescription ?? e.message ?? undefined;
}

const CODE_REJECTED = 'That code was not accepted. Check it and try again.';

/**
 * Submit the emailed code and finish the flow.
 *
 * Sign-in and sign-up complete differently, which is easy to get wrong:
 *
 *  - sign-in completes with the account attached to `result.data`;
 *  - sign-up completes with `data` typed as void and a `SignUpCompletedState`
 *    that is really a *continuation* state. Tokens only exist after calling
 *    `signIn()` on it. Treating that state as terminal yields no account and
 *    looks, from the outside, exactly like a rejected code.
 */
export async function submitCode(state: CodeState, code: string): Promise<SubmitOutcome> {
    const result = await state.submitCode(code);

    if (result.isCompleted()) {
        return finish(result.state, (result as { data?: unknown }).data);
    }

    // A sign-up flow can demand profile attributes. We configure none, but the
    // tenant's flow can still ask, and silently failing here is indefensible.
    if ('isAttributesRequired' in result && result.isAttributesRequired()) {
        const attrState = result.state;
        const required = attrState.getRequiredAttributes().map((a) => a.name ?? '').filter(Boolean);
        console.warn('sign-up requires attributes:', required);

        const filled = await attrState.submitAttributes(
            Object.fromEntries(required.map((name) => [name, deriveAttribute(name)])),
        );
        if (filled.isCompleted()) return finish(filled.state, undefined);
        return {
            ok: false,
            message: describe(filled.error, CODE_REJECTED),
            detail: rawDetail(filled.error),
        };
    }

    if (result.error) {
        return {
            ok: false,
            message: describe(result.error, CODE_REJECTED),
            detail: rawDetail(result.error),
        };
    }

    // No error, but not a state we finish on. Say which one, so the next report
    // is diagnosable instead of another round of guessing.
    const stateType = (result.state as { stateType?: string })?.stateType ?? 'unknown';
    console.warn('unhandled auth state after code submit:', stateType, result);
    return {
        ok: false,
        message: `Sign-in stopped at an unexpected step (${stateType}). Tell Barrett - this is a bug, not your code.`,
    };
}

/** Turn a completed state into an account, continuing the flow if it needs it. */
async function finish(state: unknown, data: unknown): Promise<SubmitOutcome> {
    const typedData = data as
        | { getAccount?: () => AccountInfo; getIdToken?: () => string | undefined }
        | undefined;
    const account = typedData?.getAccount?.();
    if (account) return { ok: true, account, idToken: typedData?.getIdToken?.() };

    // Sign-up: exchange the continuation state for real tokens. Pass the same
    // scopes as the sign-in path - otherwise getIdToken() below returns
    // undefined and sign-up users hit the identical silent loop.
    const continuation = state as {
        signIn?: (inputs?: { scopes?: string[] }) => Promise<unknown>;
    };
    if (typeof continuation.signIn === 'function') {
        const signedIn = (await continuation.signIn({ scopes: loginRequest.scopes })) as {
            isCompleted?: () => boolean;
            data?: { getAccount?: () => AccountInfo; getIdToken?: () => string | undefined };
            error?: unknown;
        };
        if (signedIn.isCompleted?.()) {
            return {
                ok: true,
                account: signedIn.data?.getAccount?.() ?? null,
                idToken: signedIn.data?.getIdToken?.(),
            };
        }
        return {
            ok: false,
            message: describe(signedIn.error, 'We verified the code but could not sign you in.'),
            detail: rawDetail(signedIn.error),
        };
    }

    return { ok: true, account: null };
}

/** Best-effort values for attributes a flow insists on but we never asked for. */
function deriveAttribute(name: string): string {
    if (/city|country|state|postal/i.test(name)) return 'US';
    return 'Lowe’s associate';
}

/** The account already signed in on this device, if any. */
export function currentAccount(
    client: ICustomAuthPublicClientApplication,
): AccountInfo | null {
    const result = client.getCurrentAccount();
    return result.isCompleted() ? (result.data?.getAccount() ?? null) : null;
}

export async function signOut(client: ICustomAuthPublicClientApplication): Promise<void> {
    const result = client.getCurrentAccount();
    if (result.isCompleted() && result.data) {
        await result.data.signOut();
    }
}

export type ResendOutcome =
    | { ok: true; state: CodeState; codeLength: number }
    | { ok: false; message: string };

/**
 * Ask for a fresh code.
 *
 * This RETURNS A NEW STATE and the caller must adopt it. Entra ties each code to
 * the flow that issued it, so submitting a new code against the previous state
 * fails with AADSTS501811 - which reads to the user as "the code is wrong" when
 * in fact the app was asking the wrong question.
 */
export async function resendCode(state: CodeState): Promise<ResendOutcome> {
    const result = await state.resendCode();
    if (result.isCodeRequired()) {
        return {
            ok: true,
            state: result.state,
            codeLength: result.state.getCodeLength(),
        };
    }
    return {
        ok: false,
        message: describe(result.error, 'We could not send another code. Try again shortly.'),
    };
}
